import { isAriaHidden } from '@lbcamden/lib-react';
import { useIsFetching } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router';

const hasNotScrolled = () => window.scrollY === 0;

/**
 * Ensure screen readers announce the page heading on loading a page.
 * If an assertive alert is present (e.g. toast) then skip focusing
 * the heading and focus the alert to ensure it is announced.
 * * */
export const useFocusHeading = () => {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();

  const isFetching = useIsFetching();

  const [hasFocused, setHasFocused] = useState(false);

  const focus = () => {
    const assertiveAlert = document.querySelector<HTMLElement>('[aria-live="assertive"]');
    const hashElement = hash && document.querySelector<HTMLElement>(hash);
    const heading =
      document.querySelector('h1') ||
      document.querySelector('h2,h3,h4,h5') ||
      document.querySelector('[role="heading"]');
    const focusElement = assertiveAlert || hashElement || heading;

    if (focusElement && !isAriaHidden(focusElement)) {
      focusElement.tabIndex = -1;
      focusElement.focus();
      setHasFocused(true);
    }
  };

  useEffect(() => {
    setHasFocused(false);
  }, [pathname]);

  useEffect(() => {
    const canFocus = !isFetching && !hasFocused && navType !== 'POP' && hasNotScrolled();

    if (canFocus) {
      focus();
    }
  }, [isFetching, hasFocused]);
};
